// Packages
import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import useZustandStore from 'zustandStore';

// MUI
import { useTheme } from '@mui/material/styles';
import { Box, Divider } from '@mui/material';

// Components
import { Loading } from 'components/Loading';
import TableField from 'components/Table';
import PendingChildren from './components/PendingChildren';
import OverflowTooltip from './components/OverflowTooltip';
import GlobalAlert from 'components/Notification';
import { SolidButton } from 'components/buttons/SolidButton';
import SearchInput from 'components/SearchInput';
import CreateAndEditModal from './Modal/CreateAndEditModal';
import RemovalModal from './Modal/RemovalModal';

// IMG
import { ReactComponent as AddIcon } from 'assets/images/icons/icn_btn_add.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/btn_edit.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';

// Hook
import useAuthScope from 'hooks/useAuthScope';
import useDataSourceManager from 'hooks/useDataSourceManager';
import useTableInstance from 'hooks/useTableInstance';

// Helper
import { getMainSendRequestParams, getInitTableData, getCleanPayload } from './helper';

// Const
import { defaultAlertInfo, defaultActions } from './const';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const LdapServer = ({ showEditGeneral, onLdapProfileCountChange }) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    create: uuidv4(),
    update: uuidv4(),
    remove: uuidv4(),
  }));
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [originTableData, setOriginTableData] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const [actions, setActions] = useState(defaultActions);
  const [selectedLdap, setSelectedLdap] = useState(null);

  const { scopeReady } = useOutletContext();
  const serviceStatus = useZustandStore(state => state.serviceStatus);

  const getMainTableColumns = () => {
    return [
      columnHelper.accessor(row => row.name ?? '-', {
        id: 'name',
        header: 'Profile Name',
        cell: info => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OverflowTooltip title={info.getValue()} maxWidth="200px">
              {info.getValue()}
            </OverflowTooltip>
          </Box>
        ),
      }),
      columnHelper.accessor(row => row.server ?? '-', {
        id: 'server',
        header: 'LDAP Server',
        cell: info => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor(row => row.port ?? '-', {
        id: 'port',
        header: 'Port',
        cell: info => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor(row => row.authMode ?? '-', {
        id: 'authMode',
        header: 'Authentication Mode',
        cell: info => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.display({
        id: 'action',
        header: 'Action',
        cell: info => {
          const selectedTarget = info.row.original;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Box
                component="button"
                aria-label="Edit"
                onClick={() => {
                  setSelectedLdap(selectedTarget);
                  setActions({ ...defaultActions, isEdit: true });
                }}
                sx={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <EditIcon />
              </Box>
              {showEditGeneral && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ height: '20px', alignSelf: 'center', margin: '2px' }} />
                  <Box
                    component="button"
                    aria-label="delete"
                    onClick={() => {
                      setSelectedLdap(selectedTarget);
                      setActions({ ...defaultActions, isRemove: true });
                    }}
                    sx={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <DeleteIcon />
                  </Box>
                </>
              )}
            </Box>
          );
        },
      }),
    ];
  };

  const mainColumns = useMemo(() => getMainTableColumns(), [auth, theme]);

  const mainTableInstance = useTableInstance({
    tableName: 'LdapServer',
    data: tableData,
    columns: mainColumns,
    enableSorting: true,
    enableRowSelection: true,
    enablePagination: true,
    enableGlobalFilter: true,
  });

  const fetchMainTableData = () => {
    setIsLoading(true);

    if (isAuthReady) {
      const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: 'read' });

      sendRequest(apiUrl, uuids.read, requestParams);
    } else {
      setIsLoading(false);
    }
  };

  const handleSearchChange = string => {
    setSearchValue(string);

    if (string.trim() === '') {
      setTableData(cloneDeep(originTableData));
      return;
    }

    const filteredData = originTableData.filter(item => {
      const name = item.name ? String(item.name).toLowerCase() : '';
      const nameMatch = name.includes(string.toLowerCase());
      return nameMatch;
    });

    setTableData(filteredData);
  };

  const onCreate = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: 'create' });
    const payload = getCleanPayload({ selectedLdap });

    sendRequest(apiUrl, uuids.create, requestParams, payload);
  };

  const onUpdate = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: 'update', params: { ldapId: selectedLdap.id } });
    const payload = getCleanPayload({ selectedLdap });

    sendRequest(apiUrl, uuids.update, requestParams, payload);
  };

  const onRemove = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: 'delete' });
    const payload = { profileLdapIds: [selectedLdap.id] };

    sendRequest(apiUrl, uuids.remove, requestParams, payload);
  };

  useEffect(() => {
    if (!scopeReady || !serviceStatus) return;

    if (isAuthReady) fetchMainTableData();
  }, [isAuthReady]);

  useEffect(() => {
    if (actions.isCreateAndSave) onCreate();
    else if (actions.isEditAndSave) onUpdate();
    else if (actions.isRemoveAndSave) onRemove();
  }, [actions]);

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const response = getData(uuids.read);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (checkMockup) {
        const initTableData = getInitTableData({ responseData });
        setOriginTableData(initTableData);
        setTableData(initTableData);
        onLdapProfileCountChange?.(initTableData.length);
      } else {
        if (responseData.hasOwnProperty('code') && responseData.code !== 200) {
          setAlertInfo({ open: true, severity: 'error', message: responseData.error?.message || 'Failed to get the LDAP server list.' });
        } else {
          const initTableData = getInitTableData({ responseData });
          setOriginTableData(initTableData);
          setTableData(initTableData);
          onLdapProfileCountChange?.(initTableData.length);
        }
      }
    }
    setIsLoading(false);
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.create]) return;

    const response = getData(uuids.create);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty('code') && responseData.code !== 200) {
        setAlertInfo({ open: true, severity: 'error', message: responseData.error?.message || 'Failed to create the LDAP profile.' });
        setActions({ ...defaultActions, isCreate: true, isCreateAndSave: false });
      } else {
        fetchMainTableData();
        setActions(defaultActions);
      }

      setSelectedLdap(null);
    }

    setIsLoading(false);
  }, [dataMap?.[uuids.create]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const response = getData(uuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty('code') && responseData.code !== 200) {
        setAlertInfo({ open: true, severity: 'error', message: responseData.error?.message || 'Failed to update the LDAP profile.' });
        setActions({ ...defaultActions, isEdit: true, isEditAndSave: false });
      } else {
        fetchMainTableData();
        setActions(defaultActions);
      }

      setSelectedLdap(null);
    }

    setIsLoading(false);
  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if (!dataMap?.[uuids.remove]) return;

    const response = getData(uuids.remove);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty('code') && responseData.code !== 200) {
        setAlertInfo({ open: true, severity: 'error', message: responseData.error?.message || 'Failed to remove the LDAP profile.' });
      } else {
        fetchMainTableData();
      }

      setActions(defaultActions);
      setSelectedLdap(null);
    }

    setIsLoading(false);
  }, [dataMap?.[uuids.remove]]);

  if (!scopeReady || !serviceStatus) return <Loading />;

  return (
    <>
      <Box sx={{ padding: '20px' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '80px', gap: 3 }}>
            <Divider sx={{ width: '1px', height: '40px', margin: '20px 0' }} orientation="vertical" flexItem />

            <SearchInput value={searchValue} onChange={handleSearchChange} placeholder="Search by Name" />

            {showEditGeneral && (
              <SolidButton sx={{ whiteSpace: 'nowrap' }} startIcon={<AddIcon />} width="auto" height="35px" onClick={() => setActions({ ...defaultActions, isCreate: true })}>
                Create LDAP Profile
              </SolidButton>
            )}
          </Box>
        </Box>

        <TableField {...mainTableInstance} pendingChildren={<PendingChildren {...{ isLoading, list: tableData, description: 'No data' }} />} />
      </Box>

      {(actions.isCreate || actions.isEdit) && <CreateAndEditModal {...{ selectedLdap, setSelectedLdap, actions, setActions, showEditGeneral }} />}

      {selectedLdap && actions.isRemove && (
        <RemovalModal
          {...{
            isOpenRemovalModal: actions.isRemove,
            selectedLdap,
            removeCallbacks: {
              removeCancel: () => {
                setSelectedLdap(null);
                setActions({ ...defaultActions, isRemove: false });
              },
              removeConfirm: () => {
                setActions({ ...defaultActions, isRemoveAndSave: true });
              },
            },
          }}
        />
      )}

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))} />
    </>
  );
};

export default LdapServer;
